import envConfig from './env-config';

const configs = {

    // Standing duration at origin and destination in milliseconds
    standingDuration: 60000,

    // Minimum standing duration in milliseconds
    minStandingDuration: 30000,

    // Minimum bus standing duration in milliseconds
    minBusStandingDuration: 15000,

    // Additional dwell time (ms) added to a train's standing duration based on
    // live platform crowd level ('l'/'m'/'h', from RailFeedPoller#getCrowdLevel).
    // Unknown/missing data ('l' or null) adds no delay.
    crowdDwellDelay: {l: 0, m: 10000, h: 25000},

    // Interval of refreshing object positions in milliseconds
    refreshInterval: 60000,

    // All object positions will be refreshed if the screen has been inactive for this duration
    refreshTimeout: 10000,

    // Interval of checking train and bus positions based on real-time data in milliseconds
    realtimeCheckInterval: 15000,

    // Interval of checking flight positions (5 min — fits OpenSky 400 credits/day limit)
    flightCheckInterval: 300000,

    // Maximum train speed in km/h
    maxSpeedKMPH: 80,

    // Train acceleration in km/h/s
    accelerationKMPHPS: 3,

    // Maximum train speed in km/ms
    get maxSpeed() {
        return configs.maxSpeedKMPH / 3600000;
    },

    // Train acceleration in km/ms^2
    get acceleration() {
        return configs.accelerationKMPHPS / 3600000000;
    },

    // Time required to reach maximum train speed in milliseconds
    get maxAccelerationTime() {
        return configs.maxSpeed / configs.acceleration;
    },

    // Distance required to reach maximum train speed in kilometers
    get maxAccDistance() {
        return configs.maxAccelerationTime * configs.maxSpeed / 2;
    },

    // Maximum flight speed in km/h (reduced for visual PoC — 25km path visible ~18 min)
    maxFlightSpeedKMPH: 80,

    // Flight acceleration in km/h/s
    flightAccelerationKMPHPS: 3,

    // Maximum flight speed in km/ms
    get maxFlightSpeed() {
        return configs.maxFlightSpeedKMPH / 3600000;
    },

    // Flight acceleration in km/ms^2
    get flightAcceleration() {
        return configs.flightAccelerationKMPHPS / 3600000000;
    },

    // Maximum bus speed in km/h
    maxBusSpeedKMPH: 30,

    // Bus acceleration in km/h/s
    busAccelerationKMPHPS: 3,

    // Maximum bus speed in km/ms
    get maxBusSpeed() {
        return configs.maxBusSpeedKMPH / 3600000;
    },

    // Bus acceleration in km/ms^2
    get busAcceleration() {
        return configs.busAccelerationKMPHPS / 3600000000;
    },

    // Time required to reach maximum bus speed in milliseconds
    get maxBusAccelerationTime() {
        return configs.maxBusSpeed / configs.busAcceleration;
    },

    // Distance required to reach maximum bus speed in kilometers
    get maxBusAccDistance() {
        return configs.maxBusAccelerationTime * configs.maxBusSpeed / 2;
    },

    // Delay in milliseconds for minimizing precision error
    minDelay: 25000,

    // Minimum flight interval in milliseconds
    minFlightInterval: 90000,

    // Time allotted for transitions to complete
    transitionDuration: 300,

    // Fade duration when an object is added or removed
    fadeDuration: 1000,

    // Origin of coordinates (Singapore City Hall MRT)
    defaultCenter: [103.8519, 1.2929],

    // Default zoom level (higher for smaller Singapore area)
    defaultZoom: 12,

    // Default bearing (rotation) of the map
    defaultBearing: 0,

    // Default pitch in degrees
    defaultPitch: 60,

    // Default frame rate for train and aircraft animations in the Eco mode
    defaultEcoFrameRate: 1,

    // Default view mode
    defaultViewMode: 'ground',

    // Default visual theme
    defaultTheme: 'clean-minimalist',

    // Default tracking mode
    defaultTrackingMode: 'position',

    // Default clock mode
    defaultClockMode: 'realtime',

    // Default clock mode
    defaultEcoMode: 'normal',

    // API URL - Singapore LTA DataMall
    apiUrl: {

        // LTA DataMall base URL
        lta: 'http://datamall2.mytransport.sg/ltaodataservice/',

        // LTA DataMall API Key - loaded from environment
        get ltaAccountKey() {
            return envConfig.ltaAccountKey;
        }

    },

    // Bus arrival URL (LTA DataMall)
    busArrivalUrl: 'http://datamall2.mytransport.sg/ltaodataservice/BusArrivalv2',

    // Train service alerts URL (LTA DataMall)
    trainAlertUrl: 'http://datamall2.mytransport.sg/ltaodataservice/TrainServiceAlerts',

    // Passenger volume URL (LTA DataMall)
    passengerVolumeUrl: 'http://datamall2.mytransport.sg/ltaodataservice/PCDRealTime',

    // Backend proxy URL - loaded from environment
    get proxyUrl() {
        return envConfig.proxyUrl;
    },

    // Default data URL (TODO: Replace with your own CDN after building data)
    dataUrl: './data',

    // Default data sources
    dataSources: [
        {
            type: 'lta',
            color: '#4CAF50'
        }
    ],

    // Route search URL (TODO: Implement Singapore route search)
    searchUrl: null,

    // Timestamp when the static data was last updated - loaded from environment
    get lastStaticUpdate() {
        return envConfig.lastStaticUpdate;
    },

    // String to show in an Mapbox's AttributionControl
    customAttribution: '<a href="https://github.com/nagix/mini-tokyo-3d">Based on Mini Tokyo 3D by Akihiko Kusanagi</a> | <a href="https://datamall.lta.gov.sg">LTA DataMall</a>',

    // Copyright string
    copyright: '© 2026 Mini Singapore 3D | Data © LTA Singapore',

    // Share URL - loaded from environment
    get shareUrl() {
        return envConfig.shareUrl;
    },

    // Supported events
    events: [
        'boxzoomcancel',
        'boxzoomend',
        'boxzoomstart',
        'click',
        'contextmenu',
        'dblclick',
        'drag',
        'dragend',
        'dragstart',
        'error',
        'load',
        'mousedown',
        'mousemove',
        'mouseout',
        'mouseover',
        'mouseup',
        'move',
        'moveend',
        'movestart',
        'pitch',
        'pitchend',
        'pitchstart',
        'resize',
        'rotate',
        'rotateend',
        'rotatestart',
        'touchcancel',
        'touchend',
        'touchmove',
        'touchstart',
        'wheel',
        'zoom',
        'zoomend',
        'zoomstart'
    ],

    // Supported languages (Singapore focus: English, Chinese, Malay, Tamil)
    langs: ['en', 'zh-Hans', 'zh-Hant', 'ms', 'ta'],

    // Mapbox access token - loaded from environment
    get mapboxAccessToken() {
        return envConfig.mapboxAccessToken;
    }

};

export default configs;
